import React, { useState, useEffect } from 'react';
import { Search, UserPlus, X, Trash2, Clock, ShieldCheck, Mail, MoreVertical, AlertTriangle, Sun, Moon } from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
const THEME_KEY = 'martech_theme';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

const THEMES = {
  light: {
    pageBg: '#ffffff',
    bg: '#f9fafb',
    card: '#ffffff',
    border: '#e5e7eb',
    text: '#111827',
    subtext: '#6b7280',
    brand: '#5B4EE8',
    brandDark: '#4338ca',
    brandSoft: '#eef2ff',
    danger: '#dc2626',
    dangerBg: '#fee2e2',
    dangerText: '#991b1b',
    success: '#059669',
    successBg: '#d1fae5',
    successText: '#065f46',
    pendingBg: '#fef3c7',
    pendingText: '#92400e',
    inputBg: '#ffffff',
    overlay: 'rgba(0,0,0,0.4)',
    roleAdminBg: '#ede9fe', roleAdminText: '#5b21b6',
    roleStaffBg: '#e0f2fe', roleStaffText: '#0369a1',
    roleOtherBg: '#f3f4f6', roleOtherText: '#374151',
  },
  dark: {
    pageBg: '#0d0e12',
    bg: '#15161c',
    card: '#1a1b23',
    border: '#2b2d38',
    text: '#f3f4f6',
    subtext: '#9ca3af',
    brand: '#7C72FF',
    brandDark: '#6a60e0',
    brandSoft: '#242440',
    danger: '#f87171',
    dangerBg: '#3a1d1d',
    dangerText: '#fca5a5',
    success: '#34d399',
    successBg: '#0f3d2e',
    successText: '#6ee7b7',
    pendingBg: '#3a2f10',
    pendingText: '#fbbf24',
    inputBg: '#1f2029',
    overlay: 'rgba(0,0,0,0.6)',
    roleAdminBg: '#2e2350', roleAdminText: '#c4b5fd',
    roleStaffBg: '#0e2a3a', roleStaffText: '#7dd3fc',
    roleOtherBg: '#25262e', roleOtherText: '#d1d5db',
  },
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never');

const TeamManagement = () => {
  const isMobile = useIsMobile();
  const [themeName, setThemeName] = useState(() => localStorage.getItem(THEME_KEY) || 'light');
  const C = THEMES[themeName];

  useEffect(() => { localStorage.setItem(THEME_KEY, themeName); }, [themeName]);
  const toggleTheme = () => setThemeName((t) => (t === 'light' ? 'dark' : 'light'));

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'staff' });
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState({ text: '', type: '' });

  const [toast, setToast] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [activeMember, setActiveMember] = useState(null);

  const token = localStorage.getItem('token') || localStorage.getItem('martech_token');
  const userRole = localStorage.getItem('role');

  const fetchMembers = async () => {
    try {
      setError('');
      const res = await fetch(`${API_BASE}/api/v1/team`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setMembers(data.members);
      else setError(data.error || 'Failed to load team');
    } catch (err) {
      setError('Network error loading team');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMembers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const roleColor = (role) => {
    if (role === 'admin') return { bg: C.roleAdminBg, color: C.roleAdminText };
    if (role === 'staff') return { bg: C.roleStaffBg, color: C.roleStaffText };
    return { bg: C.roleOtherBg, color: C.roleOtherText };
  };

  const statusColor = (status) =>
    status === 'pending'
      ? { bg: C.pendingBg, color: C.pendingText, label: 'Pending' }
      : { bg: C.successBg, color: C.success, label: 'Active' };

  const stats = {
    total: members.length,
    admins: members.filter((m) => m.role === 'admin').length,
    staff: members.filter((m) => m.role === 'staff').length,
    pending: members.filter((m) => m.status === 'pending').length,
  };

  const filteredMembers = members.filter((m) => {
    const matchesSearch = m.email.toLowerCase().includes(search.toLowerCase());
    const matchesRole = roleFilter === 'all' || m.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setInviteMsg({ text: '', type: '' });
    try {
      const res = await fetch(`${API_BASE}/api/v1/team/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(inviteForm),
      });
      const data = await res.json();
      if (data.success) {
        setInviteMsg({ text: data.message, type: 'success' });
        setInviteForm({ email: '', role: 'staff' });
        fetchMembers();
        setTimeout(() => setShowInviteModal(false), 1200);
      } else {
        setInviteMsg({ text: data.error, type: 'error' });
      }
    } catch (err) {
      setInviteMsg({ text: 'Network error', type: 'error' });
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (member) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/team/${member.id}/resend-invite`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) showToast(data.message);
      else showToast(data.error || 'Could not resend invite', 'error');
    } catch (err) {
      showToast('Network error', 'error');
    }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/team/${id}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role: newRole }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Role updated');
        fetchMembers();
      } else {
        showToast(data.error || 'Could not update role', 'error');
      }
    } catch (err) {
      showToast('Network error', 'error');
    }
  };

  const handleRemove = async (member) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/team/${member.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        showToast('Member removed');
        fetchMembers();
      } else {
        showToast(data.error || 'Could not remove member', 'error');
      }
    } catch (err) {
      showToast('Network error', 'error');
    } finally {
      setConfirmAction(null);
      setActiveMember(null);
    }
  };

  // ---------- shared bits ----------

  const StatCard = ({ label, value, color }) => (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '1.1rem' }}>
      <div style={{ fontSize: '0.75rem', color: C.subtext, fontWeight: '600', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '0.03em' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: '700', color: color || C.text }}>{value}</div>
    </div>
  );

  const Badge = ({ bg, color, children }) => (
    <span style={{ background: bg, color, padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );

  const inputStyle = {
    width: '100%',
    padding: '0.65rem 0.75rem',
    borderRadius: '8px',
    border: `1px solid ${C.border}`,
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    background: C.inputBg,
    color: C.text,
  };

  const MemberActions = ({ member, inline }) => {
    if (userRole !== 'admin') return null;
    return (
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select
          value={member.role}
          onChange={(e) => handleRoleChange(member.id, e.target.value)}
          style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: `1px solid ${C.border}`, fontSize: '0.8rem', background: C.inputBg, color: C.text }}
        >
          <option value="admin">Admin</option>
          <option value="staff">Staff</option>
          <option value="viewer">Viewer</option>
        </select>
        {member.status === 'pending' && (
          <button
            onClick={() => handleResendInvite(member)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: C.brandSoft, color: C.brandDark, border: 'none', padding: '0.35rem 0.6rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}
          >
            <Mail size={14} /> {inline ? 'Resend' : ''}
          </button>
        )}
        <button
          onClick={() => setConfirmAction({ type: 'remove', member })}
          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: C.dangerBg, color: C.dangerText, border: 'none', padding: '0.35rem 0.6rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' }}
        >
          <Trash2 size={14} /> {inline ? 'Remove' : ''}
        </button>
      </div>
    );
  };

  // ---------- render ----------

  return (
    <div style={{ background: C.pageBg, minHeight: '100%', width: '100%' }}>
      <div style={{ padding: isMobile ? '1rem' : '2rem 2.5rem', width: '100%', maxWidth: '1600px', margin: '0 auto', boxSizing: 'border-box' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ margin: 0, marginBottom: '0.25rem', color: C.text }}>Team Management</h2>
            <p style={{ color: C.subtext, margin: 0 }}>Manage your team members and their access roles</p>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
            <button
              onClick={toggleTheme}
              title={themeName === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', background: C.card, border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer' }}
            >
              {themeName === 'light' ? <Moon size={18} color={C.subtext} /> : <Sun size={18} color={C.subtext} />}
            </button>
            {userRole === 'admin' && (
              <button
                onClick={() => { setShowInviteModal(true); setInviteMsg({ text: '', type: '' }); }}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: C.brand, color: '#fff', padding: '0.65rem 1.1rem', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                <UserPlus size={16} /> Invite Member
              </button>
            )}
          </div>
        </div>

        {/* Stats — fills available width */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <StatCard label="Total Members" value={stats.total} />
          <StatCard label="Admins" value={stats.admins} color={C.roleAdminText} />
          <StatCard label="Staff" value={stats.staff} color={C.roleStaffText} />
          <StatCard label="Pending Invites" value={stats.pending} color={C.pendingText} />
        </div>

        {/* Search + filters — fills available width */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: C.subtext }} />
            <input
              type="text"
              placeholder="Search by email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: '2.25rem' }}
            />
          </div>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} style={inputStyle}>
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="staff">Staff</option>
            <option value="viewer">Viewer</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={inputStyle}>
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        {error && (
          <div style={{ background: C.dangerBg, color: C.dangerText, padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: C.subtext }}>Loading team...</div>
        ) : filteredMembers.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: C.subtext, background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px' }}>
            {members.length === 0 ? 'No team members yet. Invite your first team member to get started.' : 'No members match your search or filters.'}
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {filteredMembers.map((m) => {
              const sc = statusColor(m.status);
              const rc = roleColor(m.role);
              return (
                <div
                  key={m.id}
                  onClick={() => setActiveMember(m)}
                  style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '1rem', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <div style={{ fontWeight: '600', fontSize: '0.95rem', wordBreak: 'break-word', color: C.text }}>{m.email}</div>
                    <MoreVertical size={18} color={C.subtext} />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
                    <Badge bg={rc.bg} color={rc.color}>{m.role}</Badge>
                    <Badge bg={sc.bg} color={sc.color}>{sc.label}</Badge>
                  </div>
                  <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: C.subtext, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <Clock size={12} /> Joined {fmtDate(m.created_at)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden', width: '100%' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                <thead style={{ background: C.bg }}>
                  <tr>
                    {['Email', 'Role', 'Status', 'Joined', 'Last Active', 'Actions'].map((h) => (
                      <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '600', color: C.subtext, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((m, i) => {
                    const sc = statusColor(m.status);
                    const rc = roleColor(m.role);
                    return (
                      <tr key={m.id} style={{ borderTop: i === 0 ? 'none' : `1px solid ${C.border}` }}>
                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.9rem', color: C.text }}>{m.email}</td>
                        <td style={{ padding: '0.75rem 1rem' }}><Badge bg={rc.bg} color={rc.color}>{m.role}</Badge></td>
                        <td style={{ padding: '0.75rem 1rem' }}><Badge bg={sc.bg} color={sc.color}>{sc.label}</Badge></td>
                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.85rem', color: C.subtext }}>{fmtDate(m.created_at)}</td>
                        <td style={{ padding: '0.75rem 1rem', fontSize: '0.85rem', color: C.subtext }}>{fmtDateTime(m.last_login)}</td>
                        <td style={{ padding: '0.75rem 1rem' }}><MemberActions member={m} inline /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Invite modal */}
        {showInviteModal && (
          <div
            onClick={() => setShowInviteModal(false)}
            style={{ position: 'fixed', inset: 0, background: C.overlay, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 100 }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ background: C.card, borderRadius: isMobile ? '16px 16px 0 0' : '12px', padding: '1.5rem', width: isMobile ? '100%' : '420px', maxWidth: '100%' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: C.text }}>Invite New Member</h3>
                <button onClick={() => setShowInviteModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={20} color={C.subtext} />
                </button>
              </div>
              <form onSubmit={handleInvite}>
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.875rem', fontWeight: '500', color: C.text }}>Email Address</label>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="staff@hospital.com"
                  style={{ ...inputStyle, marginBottom: '1rem' }}
                />
                <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.875rem', fontWeight: '500', color: C.text }}>Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  style={{ ...inputStyle, marginBottom: '1.25rem' }}
                >
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
                <button
                  type="submit"
                  disabled={inviting}
                  style={{ width: '100%', background: C.brand, color: '#fff', padding: '0.7rem', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: inviting ? 'not-allowed' : 'pointer' }}
                >
                  {inviting ? 'Sending Invite...' : 'Send Invite'}
                </button>
                {inviteMsg.text && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', borderRadius: '6px', background: inviteMsg.type === 'success' ? C.successBg : C.dangerBg, color: inviteMsg.type === 'success' ? C.successText : C.dangerText, fontSize: '0.85rem' }}>
                    {inviteMsg.text}
                  </div>
                )}
              </form>
            </div>
          </div>
        )}

        {/* Mobile detail drawer */}
        {isMobile && activeMember && (
          <div
            onClick={() => setActiveMember(null)}
            style={{ position: 'fixed', inset: 0, background: C.overlay, display: 'flex', alignItems: 'flex-end', zIndex: 100 }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{ background: C.card, borderRadius: '16px 16px 0 0', padding: '1.5rem', width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, wordBreak: 'break-word', fontSize: '1rem', color: C.text }}>{activeMember.email}</h3>
                <button onClick={() => setActiveMember(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={20} color={C.subtext} />
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <Badge bg={roleColor(activeMember.role).bg} color={roleColor(activeMember.role).color}>{activeMember.role}</Badge>
                <Badge bg={statusColor(activeMember.status).bg} color={statusColor(activeMember.status).color}>{statusColor(activeMember.status).label}</Badge>
              </div>
              <div style={{ fontSize: '0.85rem', color: C.subtext, marginBottom: '0.4rem' }}>Joined {fmtDate(activeMember.created_at)}</div>
              <div style={{ fontSize: '0.85rem', color: C.subtext, marginBottom: '1.25rem' }}>Last active: {fmtDateTime(activeMember.last_login)}</div>
              <MemberActions member={activeMember} />
            </div>
          </div>
        )}

        {/* Confirm remove modal */}
        {confirmAction?.type === 'remove' && (
          <div
            onClick={() => setConfirmAction(null)}
            style={{ position: 'fixed', inset: 0, background: C.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{ background: C.card, borderRadius: '12px', padding: '1.5rem', width: '360px', maxWidth: '90%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <AlertTriangle size={20} color={C.danger} />
                <h3 style={{ margin: 0, fontSize: '1rem', color: C.text }}>Remove team member?</h3>
              </div>
              <p style={{ fontSize: '0.9rem', color: C.subtext, marginBottom: '1.25rem' }}>
                This will permanently remove <strong>{confirmAction.member.email}</strong> from your team. This can't be undone.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button onClick={() => setConfirmAction(null)} style={{ padding: '0.55rem 1rem', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.card, color: C.text, cursor: 'pointer', fontSize: '0.85rem' }}>
                  Cancel
                </button>
                <button onClick={() => handleRemove(confirmAction.member)} style={{ padding: '0.55rem 1rem', borderRadius: '6px', border: 'none', background: C.danger, color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' }}>
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div style={{ position: 'fixed', bottom: '1.5rem', left: '50%', transform: 'translateX(-50%)', background: toast.type === 'error' ? C.danger : C.text, color: themeName === 'light' ? '#fff' : C.pageBg, padding: '0.7rem 1.25rem', borderRadius: '999px', fontSize: '0.85rem', zIndex: 300, display: 'flex', alignItems: 'center', gap: '0.4rem', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}>
            {toast.type !== 'error' && <ShieldCheck size={16} />}
            {toast.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;